import { consoleEvents } from '@pb/utils'

import appActions from 'App/duck/actions'
import OPTIMIZELY from 'constants/optimizely'
import appSelectors from 'App/duck/selectors'
import { CHECKOUT_TRACKING_EVENTS } from 'features/checkout/constants'
import {
  fetchContextFail,
  fetchContextStart,
  fetchContextSuccess,
  ABTestContextSuccess,
  fetchSettingsSuccess,
  setShouldShowProductsDetails,
  setHasUndeliverableItems,
} from 'features/checkout/duck/actions'
import { operationFetchCities } from 'features/checkout/duck/operations'
import {
  normalizeInitContext,
  normalizeUser,
  normalizeSettings,
} from 'features/checkout/duck/normalize'
import checkoutSelectors, {
  getHasUndeliverableHazmatItems,
  getIsSplitHMOrder,
} from 'features/checkout/duck/selectors'
import { fetchContext, fetchSettings } from 'features/checkout/requests'
import { Item } from 'features/checkout/types'
import { setOrderTotals } from 'features/orderTotals/duck/actions'
import { setAppliedVouchers } from 'features/vouchers/duck/actions'
import { trackOptimizelyEvent } from 'helpers/optimizely'
import { CLICKTALE_SIGNALS } from 'helpers/tracking'
import { ThunkResult } from 'redux/types'

import { operationInitPayments } from './initPayments'
import { trackViewEvent, trackingUtilsEvents, trackingUtilsEventsForResell } from './track'

export const onFetchContext =
  (context: any): ThunkResult<Promise<void>> =>
  async (dispatch, getState) => {
    const { ...normalizedInitContext } = normalizeInitContext(context)
    dispatch(appActions.updateUser(normalizeUser(context.customer)))
    dispatch(fetchContextSuccess(normalizedInitContext))
    const state = getState()
    const isSplitOrder = getIsSplitHMOrder(state)
    const isDropShip = checkoutSelectors.getIsDropShip(state)
    const isCrossBrands = checkoutSelectors.getIsCrossBrandsBasket(state)
    const hasMultipleParcels = checkoutSelectors.getHasMultipleParcels(state)
    const getResellItems = checkoutSelectors.getResellItemsInCart(state)
    const hasUndeliverableHazmatItems = getHasUndeliverableHazmatItems(state)

    trackingUtilsEvents(isCrossBrands, isDropShip, hasMultipleParcels, getResellItems.length > 0)
    getResellItems.forEach((item: Item) => {
      trackingUtilsEventsForResell(item.condition || '')
    })

    trackOptimizelyEvent(OPTIMIZELY.SET_ORDER_TYPE, undefined, {
      isSplitOrder,
      isDropShip,
      isCrossBrands,
    })
    trackOptimizelyEvent(OPTIMIZELY.SET_VISIT_CHECKOUT_EVENT, undefined, {
      visitedCheckout: true,
    })
    // @ts-expect-error ts-migrate(2554) FIXME: Expected 0 arguments, but got 1.
    dispatch(ABTestContextSuccess(normalizedInitContext))
    dispatch(operationInitPayments(normalizedInitContext.paymentData))
    dispatch(trackViewEvent(CHECKOUT_TRACKING_EVENTS.pageView.id, CLICKTALE_SIGNALS.start))
    dispatch(setOrderTotals(context.orderTotals))
    dispatch(setAppliedVouchers(context.cart.vouchersApplied || []))

    if (hasUndeliverableHazmatItems) {
      dispatch(setShouldShowProductsDetails(true))
      dispatch(setHasUndeliverableItems(true))
    }
  }

export const operationFetchContext =
  (): ThunkResult<Promise<void>> => async (dispatch, getState) => {
    dispatch(fetchContextStart())
    try {
      const state = getState()
      const checkoutSettings = await fetchSettings()
      const normalizedSettings = normalizeSettings(checkoutSettings)
      const provinces = appSelectors.getProvinces(state)
      dispatch(fetchSettingsSuccess(normalizedSettings))
      const context = await fetchContext()
      dispatch(onFetchContext(context))

      // Fetch cities when you have single province and it's not even shown (eg: Taiwan)
      if (provinces && Object.keys(provinces).length === 1) {
        const value = Object.values(provinces)[0]
        dispatch(operationFetchCities({ provinces, value }))
      }
    } catch (error) {
      consoleEvents.error('Failed to fetch context', error)
      dispatch(fetchContextFail({}))
    }
  }
